import React, { Suspense, lazy, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';
import { toast } from '../components/ui/sonner';
import NotificationBell from '../components/NotificationBell';
import RoleSwitcher from '../components/RoleSwitcher';
import { ShieldCheck, LogOut, KeyRound, Loader2 } from 'lucide-react';

const OrgHrisConsole = lazy(() => import('../components/hris/OrgHrisConsole'));

/** Standalone HR portal for role="hr" users — the org's HRIS, nothing else. */
export default function HrPortalPage() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();

  if (user?.requires_password_change) {
    return <FirstLoginPassword user={user} onDone={(u) => login?.(null, u)} />;
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="hr-portal">
      <header className="bg-slate-950 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <div className="p-2 rounded-xl bg-indigo-600"><ShieldCheck className="w-5 h-5" /></div>
          <div className="flex-1">
            <p className="font-bold leading-tight">scensob<span className="font-light">connect</span> <span className="text-indigo-300 text-xs uppercase tracking-widest ml-2">HR Portal</span></p>
            <p className="text-[11px] text-white/60">{user?.full_name} · {user?.email}</p>
          </div>
          <NotificationBell />
          <RoleSwitcher tone="dark" />
          <button onClick={() => { logout(); navigate('/login'); }} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20" data-testid="hr-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Suspense fallback={<div className="text-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}>
          <OrgHrisConsole />
        </Suspense>
      </main>
    </div>
  );
}

function FirstLoginPassword({ user, onDone }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error('Passwords do not match');
    setBusy(true);
    try {
      await apiCall('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ new_password: pw }) });
      toast.success('Password set — welcome to the HR portal');
      const me = await apiCall('/api/auth/me');
      onDone(me.user || me);
    } catch (err) { toast.error(err.message || 'Could not update password'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <form onSubmit={submit} className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl space-y-4" data-testid="hr-first-login">
        <div className="p-3 rounded-2xl bg-indigo-50 text-indigo-700 w-fit"><KeyRound className="w-6 h-6" /></div>
        <h1 className="text-2xl font-bold text-slate-900">Set your password</h1>
        <p className="text-sm text-slate-600">Hi {user?.full_name?.split(' ')[0]} — you signed in with a temporary password. Choose a new one to continue to your organisation's HR portal.</p>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} minLength={8} required placeholder="New password (min 8 characters)" autoComplete="new-password"
          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg" data-testid="hr-new-password" />
        <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} minLength={8} required placeholder="Confirm new password" autoComplete="new-password"
          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg" data-testid="hr-new-password-confirm" />
        <button type="submit" disabled={busy} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50" data-testid="hr-set-password">
          {busy ? 'Saving…' : 'Save and continue'}
        </button>
      </form>
    </div>
  );
}
